/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import {GenericObject} from '@ion/core/src/types/entities';

type FrameInstance = GenericObject & {};

/**
 * Manages references to iframe SDK instances in a safe and centralized way.
 *
 * `DashboardFrameManager` is a singleton class that acts as a centralized registry 
 * for managing references to `<iframe>` elements used for rendering embedded
 * SDK dashboards (such as Sisense dashboards).
 * 
 * It provides utility methods to:
 * - Register an iframe reference (`setFrameInstance`)
 * - Retrieve a specific iframe by its associated ID (`getFrameInstance`)
 * - Retrieve all registered iframe instances (`getAllFrames`)
 * - Remove an individual iframe reference (`removeFrameInstance`)
 * - Clear all stored iframe references (`clearAllFrameInstances`)
 *
 * This class ensures:
 * - Clean management of multiple iframe dashboards
 * - Safe access and cleanup of iframe references to prevent memory leaks
 * - Centralized frame tracking, especially helpful in applications where
 *   dashboards are dynamically loaded or removed
 *
 * ```tsx
 * import DashboardFrameManager from '@ion/charting/src/bis/dashboard/DashboardFrameManager';
 *
 * // Register a new iframe element (usually after it's rendered)
 * DashboardFrameManager.setFrameInstance(
 *   'dashboard-1',
 *   document.getElementById('my-iframe') as HTMLIFrameElement
 * );
 *
 * // Retrieve the iframe reference later for messaging or DOM access
 * const frame = DashboardFrameManager.getFrameInstance('dashboard-1');
 *
 * // Remove a specific iframe instance when the dashboard is unmounted
 * DashboardFrameManager.removeFrameInstance('dashboard-1');
 *
 * // Clear all iframe references when navigating away or performing cleanup
 * DashboardFrameManager.clearAllFrameInstances();
 * 
 * ```
 */
class DashboardFrameManager {
  /**
   * Internal map that holds iframe instances keyed by unique string identifiers.
   * @private
   */
  private _frameRef: Record<string, FrameInstance> = {};

  /**
   * Stores a reference to an iframe instance using a unique ID.
   *
   * @param id - Unique identifier for the iframe instance.
   * @param instance - The iframe element or null to store.
   */
  public setFrameInstance(id: string, instance: FrameInstance): boolean {
    if (id) {
      this._frameRef[id] = instance;
      return true;
    }
    return false;
  };

  /**
   * Retrieves the iframe instance for the given ID.
   *
   * @param id - The unique identifier of the iframe.
   * @returns The corresponding iframe element or `null` if not found.
   */
  public getFrameInstance = (id: string): FrameInstance | undefined => this._frameRef[id];

  /**
   * Returns all registered iframe instances.
   *
   * @returns A map of all iframe instances indexed by their IDs.
   */
  public getAllFrames = (): Record<string, FrameInstance> => ({...this._frameRef});

  /**
   * Removes the iframe instance associated with the given ID.
   *
   * @param id - The unique identifier of the iframe to remove.
   */
  public removeFrameInstance(id: string): void {
    if (id in this._frameRef) {
      delete this._frameRef[id];
    }
  };

  /**
   * Clears all stored iframe instances from the manager.
   */
  public clearAllFrameInstances(): void {
    Object.keys(this._frameRef).forEach(this.removeFrameInstance.bind(this));
    this._frameRef = {};
  };
}

export default new DashboardFrameManager();
